import { DownloadIcon } from "@/components/Icons"
import { P3 } from "@/components/Typographies"
import { useAccessToken } from "@/hooks/useAccessToken"
import type { MediaJsonld } from "@/networking/api"
import { openFileLink } from "@/utils/download"
import { formatFileSize, getFileExtension, getFilename } from "@/utils/files/file"
import { isBlank, isNotBlank, isNullOrUndefined } from "@/utils/ts-utils"
import { Button, type ButtonProps, Flex } from "@chakra-ui/react"

type Props = ButtonProps & {
  file?: MediaJsonld
  fileName?: string | null
  fileUrl?: string | null
  fileSize?: string | null
  fileType?: string | null
  iconSize?: string | number
  hideInfos?: boolean
  infosOnSameLine?: boolean
}

export const DownloadFileButton: React.FC<Props> = ({
  file,
  fileName,
  fileUrl,
  fileSize,
  fileType,
  iconSize,
  hideInfos,
  infosOnSameLine,
  children,
  ...props
}) => {
  const { accessToken } = useAccessToken()
  const media =
    file ??
    ({
      source: fileUrl,
      size: fileSize,
      type: fileType,
    } as MediaJsonld)

  if (isBlank(media.source)) return null

  const name = isNotBlank(fileName) ? fileName : getFilename(media.source)
  const size = formatFileSize(Number.parseInt(media?.size ?? "0"))

  if (isBlank(media.type)) media.type = getFileExtension(name)

  return (
    <Flex gap={2} flexDirection={infosOnSameLine ? "row" : "column"} alignItems="center">
      {/* A file to download is a link, not a call to action: "link" variant by default. */}
      <Button
        variant="link"
        display="flex"
        alignItems="center"
        gap="2"
        textAlign="left"
        whiteSpace="normal"
        aria-label={name}
        {...props}
        onClick={() => openFileLink(media, accessToken)}
      >
        {children}
        {isNullOrUndefined(children) && (name.length > 32 ? `${name.substring(0, 30)}...` : name)}
        <DownloadIcon boxSize={iconSize ?? 4} flexShrink={0} />
      </Button>
      {!hideInfos && (
        <P3 color="textMuted">
          ({isNotBlank(media.type) ? `${media.type.toLocaleUpperCase()}, ` : ""}
          {size})
        </P3>
      )}
    </Flex>
  )
}
